/**
 * Step-Up Authentication Component
 * 
 * Re-authentication modal for sensitive operations.
 * Displays when user attempts a protected action and doesn't have a valid step-up token.
 * 
 * Usage:
 * 1. Wrap sensitive action with useStepUpAuth hook
 * 2. The hook checks for valid token before allowing action
 * 3. If no valid token, shows this modal for re-authentication
 * 4. After successful auth, the action proceeds
 */

import React, { useState, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { Lock, Shield, Eye, EyeOff, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Action type descriptions for user-friendly display
const ACTION_DESCRIPTIONS = {
  role_change: 'Change user role or permissions',
  user_delete: 'Delete user account',
  hr_record_delete: 'Delete HR employee record',
  hr_record_edit: 'Edit sensitive HR data',
  data_export: 'Export sensitive data',
  settings_change: 'Modify system settings',
  payslip_upload: 'Upload employee payslip',
  payslip_delete: 'Delete employee payslip',
  bulk_action: 'Perform bulk administrative action',
  attendance_override: 'Override attendance records',
  leave_policy_change: 'Modify leave policies',
  recruitment_delete: 'Delete recruitment records',
};

// Context for step-up authentication state
const StepUpAuthContext = createContext(null);

/**
 * Step-Up Authentication Provider
 * Manages step-up tokens and auth state across the app
 */
export const StepUpAuthProvider = ({ children }) => {
  const [tokens, setTokens] = useState({}); // { actionType: { token, expiresAt } }
  const [pendingAction, setPendingAction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if we have a valid token for an action
  const hasValidToken = useCallback((actionType) => {
    const tokenData = tokens[actionType];
    if (!tokenData) return false;
    
    const expiresAt = new Date(tokenData.expiresAt);
    return expiresAt > new Date();
  }, [tokens]);

  // Store a new token
  const setToken = useCallback((actionType, token, expiresAt) => {
    setTokens(prev => ({
      ...prev,
      [actionType]: { token, expiresAt }
    }));
  }, []);

  // Get token for an action
  const getToken = useCallback((actionType) => {
    if (!hasValidToken(actionType)) return null;
    return tokens[actionType]?.token;
  }, [tokens, hasValidToken]);

  // Clear token for an action
  const clearToken = useCallback((actionType) => {
    setTokens(prev => {
      const newTokens = { ...prev };
      delete newTokens[actionType];
      return newTokens;
    });
  }, []);

  // Request step-up auth for an action (callback-based)
  const requestAuth = useCallback((actionType, actionDetails, onSuccess) => {
    setPendingAction({ actionType, actionDetails, onSuccess });
    setIsModalOpen(true);
  }, []);

  // Promise-based step-up auth for async/await usage
  const requireStepUp = useCallback((actionType, actionDetails) => {
    return new Promise((resolve) => {
      // Check if we already have a valid token
      const existingToken = tokens[actionType];
      if (existingToken) {
        const expiresAt = new Date(existingToken.expiresAt);
        if (expiresAt > new Date()) {
          resolve({ success: true, token: existingToken.token });
          return;
        }
      }
      
      // Need to request new auth
      setPendingAction({
        actionType,
        actionDetails,
        onSuccess: (token) => {
          resolve({ success: true, token });
        },
        onCancel: () => {
          resolve({ success: false, cancelled: true });
        }
      });
      setIsModalOpen(true);
    });
  }, [tokens]);

  // Handle successful authentication
  const handleAuthSuccess = useCallback((actionType, token, expiresAt) => {
    setToken(actionType, token, expiresAt);
    setIsModalOpen(false);
    
    if (pendingAction?.onSuccess) {
      pendingAction.onSuccess(token);
    }
    setPendingAction(null);
  }, [pendingAction, setToken]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    if (pendingAction?.onCancel) {
      pendingAction.onCancel();
    }
    setPendingAction(null);
  }, [pendingAction]);

  const value = {
    hasValidToken,
    getToken,
    setToken,
    clearToken,
    requestAuth,
    requireStepUp,
    pendingAction,
  };

  return (
    <StepUpAuthContext.Provider value={value}>
      {children}
      <StepUpAuthModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        actionType={pendingAction?.actionType}
        actionDetails={pendingAction?.actionDetails}
        onSuccess={handleAuthSuccess}
      />
    </StepUpAuthContext.Provider>
  );
};

/**
 * Hook to use step-up authentication
 */
export const useStepUpAuth = () => {
  const context = useContext(StepUpAuthContext);
  if (!context) {
    throw new Error('useStepUpAuth must be used within StepUpAuthProvider');
  }
  return context;
};

/**
 * Step-Up Authentication Modal
 */
const StepUpAuthModal = ({ isOpen, onClose, actionType, actionDetails, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  const actionDescription = ACTION_DESCRIPTIONS[actionType] || actionType;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/api/step-up-auth/authenticate`, {
        password,
        action_type: actionType,
        action_details: actionDetails
      });

      if (response.data.success) {
        toast.success('Re-authentication successful', {
          description: `Valid for ${Math.floor((new Date(response.data.expires_at) - new Date()) / 60000)} minutes`
        });
        setPassword('');
        setAttempts(0);
        onSuccess(actionType, response.data.token, response.data.expires_at);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Authentication failed';
      setError(errorMessage);
      setAttempts(prev => prev + 1);
      
      if (err.response?.status === 429) {
        toast.error('Too many failed attempts', {
          description: errorMessage
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    setAttempts(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Re-Authentication Required
          </DialogTitle>
          <DialogDescription>
            This action requires you to verify your identity.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Action info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Protected Action</p>
                  <p className="text-sm text-amber-700">{actionDescription}</p>
                  {actionDetails && (
                    <p className="text-xs text-amber-600 mt-1">{actionDetails}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Password input */}
            <div>
              <Label htmlFor="step-up-password" className="text-sm font-medium">
                Enter your password to continue
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="step-up-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className={`pr-10 ${error ? 'border-red-500' : ''}`}
                  autoFocus
                  disabled={isLoading}
                  data-testid="step-up-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Attempt warning */}
            {attempts >= 2 && (
              <div className="text-xs text-amber-600">
                ⚠️ {3 - attempts} attempt{3 - attempts !== 1 ? 's' : ''} remaining before temporary lockout
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={!password || isLoading} className="bg-blue-600 hover:bg-blue-700">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Identity
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Higher-order function to wrap sensitive actions with step-up auth
 * 
 * Usage:
 * const deleteRecord = withStepUpAuth('hr_record_delete', async (token) => {
 *   await api.delete(`/records/${id}`, { headers: { 'X-Step-Up-Token': token } });
 * });
 */
export const withStepUpAuth = (actionType, action, actionDetails = null) => {
  return async (stepUpAuth) => {
    // Check if we have a valid token
    if (stepUpAuth.hasValidToken(actionType)) {
      const token = stepUpAuth.getToken(actionType);
      return action(token);
    }

    // Request re-authentication
    return new Promise((resolve, reject) => {
      stepUpAuth.requestAuth(actionType, actionDetails, async (token) => {
        try {
          const result = await action(token);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
    });
  };
};

export default StepUpAuthModal;
